
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
    GroupNameSumPair,
    PermissionsGroup,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import moment from 'moment';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        StackedBarChartFilterable,
    },
})
export default class BpFinancialPositionLiabilitiesEquitiesChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['transactions'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
    > = ['dateAsOf'];

    public legends: string[] = [];
    public xAxisData: string[] = [];
    public series: echarts.ECharts[] | any = [];

    public get currencySymbol() {
        return gdbx.currencySymbol;
    }

    public get dateFormatted() {
        return [this.selectedAsOfDate];
    }

    public get tooltipFormatter() {
        return TooltipFormatter.assetsLiabilities;
    }

    public get dataViewFormatter() {
        return DataViewFormatter.assestsLiabilities;
    }

    public get expensiveHook() {
        const { selectedAsOfDate } = this;
        return JSON.stringify([selectedAsOfDate]);
    }

    public async expensiveCalc() {
        const table = getTableName('transactions');
        const dateAsOf = this.selectedAsOfDate;
        const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';

        const accTypeList: string[] = [
            'FA', 'IV', 'OA', 'LT', 'OL', 'CP', 'RV', 'AP',
        ];
        const accTypeNameList: string[] = [
            'FIXED ASSETS',
            'INVESTMENT',
            'OTHER ASSETS',
            'NON-CURRENT LIABILITIES',
            'OTHER LIABILITIES',
            'EQUITY',
            'RESERVE',
            'APPROPRIATION ACCOUNT',
        ];

        const caSpecialList: string[] = ['BA', 'CH', 'DP', 'DR'];
        const caSpecialNameList: string[] = [
            'BANK',
            'CASH',
            'CREDITOR\'S DEPOSIT',
            'DEBTORS',
        ];

        const clSpecialList: string[] = ['BA', 'DP', 'CR'];
        const clSpecialNameList: string[] = [
            'BANK LIABILITIES',
            'DEBTOR\'S DEPOSIT',
            'CREDITORS',
        ];

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(
            ['transactions', 'system', 'stockBalances'],
        );
        this.checkInterrupted(jobId);

        this.generateLoadingText(0.3);

        const systemTable = getTableName('system');
        const allAccTypes
            = `'FA','IV','OA','LT','OL','CP','RV','AP',`
            + `'CA','CL','SL','SA','OI','EO','CO','EP','TX','RE'`;

        const [accTypeResult, caResult, clResult, systemResult] =
            await Promise.all([
                queryHead.rawQuery(`
                    SELECT "ACCTYPE", SUM(${amt}) as total
                    FROM ${table}
                    WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${dateAsOf}
                        AND "ACCTYPE" IN (${allAccTypes})
                    GROUP BY "ACCTYPE"
                `),
                queryHead.rawQuery(`
                    SELECT "SPECIALACCTYPE",
                        SUM(${amt}) as total
                    FROM ${table}
                    WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${dateAsOf}
                        AND "ACCTYPE" = 'CA'
                        AND "SPECIALACCTYPE" IN ('BA','CH','DP','DR')
                    GROUP BY "SPECIALACCTYPE"
                `),
                queryHead.rawQuery(`
                    SELECT "SPECIALACCTYPE",
                        SUM(${amt}) as total
                    FROM ${table}
                    WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${dateAsOf}
                        AND "ACCTYPE" = 'CL'
                        AND "SPECIALACCTYPE" IN ('BA','DP','CR')
                    GROUP BY "SPECIALACCTYPE"
                `),
                queryHead.rawQuery(
                    `SELECT "VAL" FROM ${systemTable} LIMIT 1`,
                ),
            ]);
        this.checkInterrupted(jobId);

        const accTypeSums: Record<string, number> = {};
        for (const row of accTypeResult) {
            accTypeSums[row.ACCTYPE] = Number(row.total) || 0;
        }
        const caSpecialSums: Record<string, number> = {};
        for (const row of caResult) {
            caSpecialSums[row.SPECIALACCTYPE]
                = Number(row.total) || 0;
        }
        const clSpecialSums: Record<string, number> = {};
        for (const row of clResult) {
            clSpecialSums[row.SPECIALACCTYPE]
                = Number(row.total) || 0;
        }

        // CA breakdown
        const caTotal = accTypeSums.CA || 0;
        const currentAssetPair: GroupNameSumPair[] =
            caSpecialList.map((code, i) => ({
                groupName: caSpecialNameList[i],
                sum: caSpecialSums[code] || 0,
            }));
        const knownCaSum = caSpecialList
            .map((code) => caSpecialSums[code] || 0)
            .reduce((a, b) => a + b, 0);
        const otherCurrentAssetPair: GroupNameSumPair[] = [{
            groupName: 'OTHER CURRENT ASSETS',
            sum: caTotal - knownCaSum,
        }];

        // CL breakdown
        const clTotal = accTypeSums.CL || 0;
        const currentLiabilityPair: GroupNameSumPair[] =
            clSpecialList.map((code, i) => ({
                groupName: clSpecialNameList[i],
                sum: (clSpecialSums[code] || 0) * -1,
            }));
        const knownClSum = clSpecialList
            .map((code) => clSpecialSums[code] || 0)
            .reduce((a, b) => a + b, 0);
        const otherCurrentLiabilityPair: GroupNameSumPair[] = [{
            groupName: 'OTHER CURRENT LIABILITIES',
            sum: (clTotal - knownClSum) * -1,
        }];

        // Main accType pairs (negate liabilities and equity accounts)
        let ddPair: GroupNameSumPair[] = accTypeList.map(
            (code, i) => ({
                groupName: accTypeNameList[i],
                sum: code === 'LT' || code === 'CP'
                    || code === 'RV' || code === 'AP'
                    ? (accTypeSums[code] || 0) * -1
                    : accTypeSums[code] || 0,
            }),
        );

        // System + stock movement
        const startFinancialYear =
            systemResult[0] && systemResult[0].VAL;

        const stockBalance = await this._getStockMovement(queryHead, [
            moment(startFinancialYear, 'DD/MM/YYYY').valueOf(),
            this.selectedAsOfDate,
        ]);

        // Calculate retained earnings from P&L accounts
        const netSales = (accTypeSums.SL || 0)
            + (accTypeSums.SA || 0)
            + (accTypeSums.OI || 0)
            + (accTypeSums.EO || 0);
        const cogs = accTypeSums.CO || 0;
        const expenses = accTypeSums.EP || 0;
        const tax = accTypeSums.TX || 0;
        const reAccountBalance = accTypeSums.RE || 0;

        const adjustedCOGS = cogs + stockBalance;
        const grossProfit = -netSales - adjustedCOGS;
        const netProfitBeforeTax = grossProfit - expenses;
        const retainedEarningSum =
            -reAccountBalance + netProfitBeforeTax - tax;

        this.generateLoadingText(1);

        ddPair.splice(1, 0, ...currentAssetPair);
        ddPair.splice(5, 0, ...otherCurrentAssetPair);
        ddPair.splice(6, 0, {
            groupName: 'STOCK BALANCE',
            sum: -stockBalance,
        });
        ddPair.splice(10, 0, ...currentLiabilityPair);
        ddPair.splice(13, 0, ...otherCurrentLiabilityPair);
        ddPair.splice(16, 0, {
            groupName: 'RETAINED EARNING',
            sum: retainedEarningSum,
        });

        const filterNames = [
            'BANK LIABILITIES', 'CREDITORS',
            'OTHER CURRENT LIABILITIES',
            'OTHER LIABILITIES', 'EQUITY',
            'DEBTOR\'S DEPOSIT',
            'NON-CURRENT LIABILITIES',
            'RETAINED EARNING', 'RESERVE',
            'APPROPRIATION ACCOUNT',
        ];

        ddPair = ddPair.filter(
            (pair) => filterNames.includes(pair.groupName),
        );
        const equityIndex = ddPair.findIndex(
            (o) => o.groupName === 'EQUITY',
        );
        this.xAxisData = ddPair.map((pair) => pair.groupName);
        this.xAxisData.splice(equityIndex + 1, 3);

        this.series = ddPair.map((pair, i) => ({
            name: pair.groupName,
            type: 'bar',
            stack: 'assetLiabilities',
            data: ddPair.map((pair2) =>
                pair2.groupName === pair.groupName
                    ? pair.sum : 0,
            ),
        }));

        const retainedEarningIndex = equityIndex + 1;
        const reserveIndex = equityIndex + 2;
        const appropriationIndex = equityIndex + 3;

        this.series.forEach((o, i) => {
            if (i === appropriationIndex) {
                o.data!.splice(equityIndex, 3);
            } else if (i === reserveIndex) {
                o.data!.splice(appropriationIndex, 1);
                o.data!.splice(equityIndex, 2);
            } else if (i === retainedEarningIndex) {
                o.data!.splice(reserveIndex, 2);
                o.data!.splice(equityIndex, 1);
            } else {
                o.data!.splice(retainedEarningIndex, 3);
            }
        });

        const clone = ddPair.concat();
        clone.sort((a, b) => Math.abs(b.sum) - Math.abs(a.sum));
        this.legends = clone.slice(0, 6).map((o) => o.groupName);

        this.saveHistory('series', 'xAxisData', 'legends');
    }
}
